import * as React from 'react';
import type { TreeItem, TreeItemIndex } from 'react-complex-tree';
import type { ITreeNode, UseComplexTreeHandlersResult } from '../types';

/**
 * Provides event handlers for ComplexTree interaction.
 * - Provides expand/collapse handlers for externally controlled expanded state
 * - Computes full selection including descendant items when a item is selected
 * - Exposes a helper to tell if the tree is currently empty
 */
export const useComplexTreeHandlers = (
	treeItems: Record<TreeItemIndex, TreeItem<ITreeNode>>,
	commitNodeIds: (selectedNodeIds: Set<TreeItemIndex>) => void,
	expandedNodeIds: Set<TreeItemIndex>,
	setExpandedNodeIds: (expandedNodeIds: Set<TreeItemIndex>) => void
): UseComplexTreeHandlersResult => {
	const [focusedItem, setFocusedItem] = React.useState<TreeItemIndex | undefined>(undefined);
	const [previouslyClickedNodeId, setPreviouslyClickedNodeId] = React.useState<TreeItemIndex | undefined>(undefined);

	const expandItemById = React.useCallback(
		(id: TreeItemIndex) => {
			const next = new Set(expandedNodeIds);
			next.add(id);
			setExpandedNodeIds(next);
		},
		[expandedNodeIds, setExpandedNodeIds]
	);

	const onExpandItem = React.useCallback(
		(item: { index: TreeItemIndex }) => expandItemById(item.index),
		[expandItemById]
	);

	const collapseItemById = React.useCallback(
		(id: TreeItemIndex) => {
			const next = new Set(expandedNodeIds);
			next.delete(id);
			setExpandedNodeIds(next);
		},
		[expandedNodeIds, setExpandedNodeIds]
	);

	const onCollapseItem = React.useCallback(
		(item: { index: TreeItemIndex }) => collapseItemById(item.index),
		[collapseItemById]
	);

	const getItemsAndDescendantsIds = React.useCallback(
		(itemIds: TreeItemIndex[]) => {
			const resultIds = new Set<TreeItemIndex>(itemIds);
			const visitedItems = new Set<TreeItemIndex>(itemIds);
			const itemsToVisitQueue: TreeItemIndex[] = itemIds;

			while (itemsToVisitQueue.length > 0) {
				const itemId = itemsToVisitQueue.shift()!;
				const item = treeItems[itemId];

				if (item.children) {
					for (const childId of item.children) {
						if (!visitedItems.has(childId)) {
							visitedItems.add(childId);
							resultIds.add(childId);
							itemsToVisitQueue.push(childId);
						}
					}
				}
			}
			return resultIds;
		},
		[treeItems]
	);

	/**
	 * Tree Selection UX Specification:
	 *
	 * Some node must always be selected at any given point (not possible to de-select everything) -> makes UX as a navigation tool feel better
	 * as you will always be "somewhere" within the tree
	 *
	 * BASIC SELECTION (no modifiers):
	 * 1. Click unselected node → node + all descendants selected + node expands   O
	 * 2. Click selected node (was previous click) while expanded → only node selected (descendants deselected) + node collapses    O
	 * 3. Click selected node (selected as descendant, not via click) → node + all descendants selected + node expands	O
	 *
	 * MULTI-SELECT MODIFIERS:
	 * 4. Ctrl/Cmd-click → only individual node(s) selected (no descendants, no expansion)   O
	 * 5. Ctrl/Cmd-click only selected item → nothing selected (deselect all)   O
	 * 6. Shift-click → range of individual nodes selected (no descendants, no expansion)   O
	 *
	 * MANUAL TOGGLE RELATED BEHAVIOR:
	 * 7. Manual expand/collapse → does NOT change selection   O
	 * 8. Manual expand unselected node, then click → node + descendants selected + stays expanded     O
	 * 9. Manual collapse selected node, then click same node → node + descendants selected + expands again    O
	 * 10. Click node (selects + descendants), click again (collapses to single), manual expand, click again → collapses and only node selected    O
	 * 11. Expanded unselected node with expanded descendants, then click → preserve descendant expansion states    O
	 *
	 */

	const onSelectItems = React.useCallback(
		//treeProposedSelectedItemIds is an array of the item ids the tree, from react-complex-tree, proposes to select
		//as we manage state externally, tree doesn't handle updating selection state, rather it temporarily suggests items to select
		//based on what was clicked, and then in this code-block we handle that "proposal" with custom logic, then "commit" what we
		//want per our logic
		(treeProposedSelectedItemIds: TreeItemIndex[]) => {
			if (treeProposedSelectedItemIds.length === 0) {
				setPreviouslyClickedNodeId(undefined);
				return;
			}

			//The number tree proposed items will only ever be >1 if a shift click or command click action was taken, as per
			//the design of react-complex-tree
			const wasMultiClickAction = treeProposedSelectedItemIds.length > 1;

			if (wasMultiClickAction) {
				//The default react-complex-tree logic is correct for these cases, so we commit the selection without descendant selection nor expanding
				commitNodeIds(new Set(treeProposedSelectedItemIds));
				return;
			}

			const clickedOnNodeId = treeProposedSelectedItemIds[0];
			const clickedOnSameNodeAsPreviousClick = clickedOnNodeId === previouslyClickedNodeId;
			const nodeWasExpanded = expandedNodeIds.has(clickedOnNodeId);

			setPreviouslyClickedNodeId(clickedOnNodeId);

			//As per our UX, we want to single select and close a item iff it was also the last clicked item and this item was already expanded
			if (clickedOnSameNodeAsPreviousClick && nodeWasExpanded) {
				commitNodeIds(new Set([clickedOnNodeId]));
				collapseItemById(clickedOnNodeId);
				return;
			}

			//Otherwise, select the clicked node and all its dependents, and make sure the clicked node is expanded
			//(does nothing if it already is)
			const itemWithDescendantsIds = getItemsAndDescendantsIds([clickedOnNodeId]);
			commitNodeIds(itemWithDescendantsIds);
			expandItemById(clickedOnNodeId);
		},
		[commitNodeIds, getItemsAndDescendantsIds, expandedNodeIds, expandItemById, collapseItemById, previouslyClickedNodeId]
	);

	const onFocusItem = React.useCallback(
		(item: { index: TreeItemIndex }) => {
			setFocusedItem(item.index);
		},
		[setFocusedItem]
	);

	const isTreeEmpty = React.useCallback(() => {
		if (!treeItems || Object.keys(treeItems).length === 0) return true;
		const rootItem = treeItems.root;
		return !rootItem || !rootItem.children || rootItem.children.length === 0;
	}, [treeItems]);

	const treeItemIdSet: Set<TreeItemIndex> = React.useMemo(() => new Set(Object.keys(treeItems)), [treeItems]);

	//This cleanup hook runs if the tree structure changes, removing nodes from expandedNodeIds that no longer exist within the tree
	React.useEffect(() => {
		if (expandedNodeIds.size === 0) return;
		const validExpandedItems = new Set<TreeItemIndex>();
		let hasInvalidItems = false;
		for (const itemId of expandedNodeIds) {
			if (treeItemIdSet.has(itemId)) {
				validExpandedItems.add(itemId);
			} else {
				hasInvalidItems = true;
			}
		}
		if (hasInvalidItems) {
			setExpandedNodeIds(validExpandedItems);
		}
	}, [treeItemIdSet, expandedNodeIds, setExpandedNodeIds]);

	return {
		onSelectItems,
		onExpandItem,
		onCollapseItem,
		onFocusItem,
		focusedItem,
		isTreeEmpty,
	};
};
